import { Component } from 'react';
import type { ReactNode } from 'react';
import { openFeedback } from '@/utils/feedbackBus';

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackLabel?: string;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: unknown): void {
    console.error(
      '[ErrorBoundary]',
      this.props.fallbackLabel ?? 'panel',
      error,
      info,
    );
  }

  private handleReport = (): void => {
    if (this.state.error) {
      openFeedback('bug_report', 'error', String(this.state.error));
    }
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleTryAgain = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const { fallbackLabel } = this.props;
    const message = this.state.error ? String(this.state.error) : '';

    return (
      <div role="alert" className="error-boundary">
        <h3 className="error-boundary-title">Something went wrong</h3>
        {fallbackLabel ? (
          <p className="error-boundary-context">
            This happened in the {fallbackLabel} panel.
          </p>
        ) : null}
        {message ? (
          <pre className="error-boundary-msg">{message}</pre>
        ) : null}
        <div className="error-boundary-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={this.handleReport}>
            Report this error
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={this.handleReload}>
            Reload page
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={this.handleTryAgain}>
            Try again
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
